import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";

export type GridThisOrThatRaw = RawVisualGrouping & {
  type: "grid_this_or_that";
  content: string;
  rows?: { value: string }[];
  row_options: {
    value: string;
    text: string;
    image?: string | null;
    display_text: boolean;
    disabled: boolean;
    unique: boolean;
  }[][];
  dont_know_option?: { value: string; text: string } | null;
  previous_response?: string[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type GridThisOrThatDefinition = VisualGrouping & {
  rowIds?: readonly string[];
  prompt: string;
  pairs: readonly (readonly {
    value: string;
    label: string;
    image?: string;
    displayLabel: boolean;
    disabled: boolean;
  }[])[];
  dontKnow?: { value: string; label: string };
  previousResponses: readonly string[];
  image?: string;
  altText?: string;
  error?: string;
};

export type GridThisOrThatDraft = {
  selectedByRow: readonly (string | null)[];
};
export type GridThisOrThatEvent =
  | {
      type: "select";
      rowIndex: number;
      value: string;
    }
  | { type: "dont-know"; rowIndex: number };
export type GridThisOrThatResponse = {
  type: "grid_this_or_that";
  value: string;
  responses: string;
};

export const gridThisOrThatCore: QuestionCore<
  GridThisOrThatRaw,
  GridThisOrThatDefinition,
  GridThisOrThatDraft,
  GridThisOrThatEvent,
  GridThisOrThatResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.row_options) ||
      raw.row_options.length === 0 ||
      raw.row_options.some((pair) => !Array.isArray(pair) || pair.length !== 2)
    ) {
      throw new Error("This-or-that requires rows with two options each.");
    }
    if (
      raw.row_options.some(
        (pair) =>
          pair.some(
            (option) => typeof option.value !== "string" || !option.value,
          ) ||
          pair[0].value === pair[1].value ||
          (raw.dont_know_option !== undefined &&
            raw.dont_know_option !== null &&
            pair.some(
              (option) => option.value === raw.dont_know_option?.value,
            )),
      )
    ) {
      throw new Error(
        "This-or-that row and don't-know option values must be distinct.",
      );
    }
    if (
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some((value) => typeof value !== "string"))
    ) {
      throw new Error(
        "This-or-that previous response must be a list of strings.",
      );
    }
    return {
      ...decodeVisualGroups(raw),
      ...(raw.rows ? { rowIds: raw.rows.map((row) => row.value) } : {}),
      prompt: raw.content,
      pairs: raw.row_options.map((pair) =>
        pair.map((option) => ({
          value: option.value,
          label: option.text,
          displayLabel: option.display_text,
          disabled: option.disabled,
          ...(option.image ? { image: option.image } : {}),
        })),
      ),
      previousResponses: raw.previous_response ?? [],
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => ({
    selectedByRow: definition.pairs.map((pair, index) => {
      const previous = definition.previousResponses[index];
      if (definition.dontKnow && previous === definition.dontKnow.value)
        return previous;
      return pair.some(
        (option) => option.value === previous && !option.disabled,
      )
        ? previous
        : null;
    }),
  }),
  update: (definition, draft, event) => {
    if (
      !Number.isInteger(event.rowIndex) ||
      event.rowIndex < 0 ||
      event.rowIndex >= definition.pairs.length
    )
      return draft;
    if (event.type === "dont-know") {
      if (!definition.dontKnow) return draft;
      const selectedByRow = [...draft.selectedByRow];
      selectedByRow[event.rowIndex] = definition.dontKnow.value;
      return { selectedByRow };
    }
    if (
      !definition.pairs[event.rowIndex].some(
        (option) => option.value === event.value && !option.disabled,
      )
    )
      return draft;
    const selectedByRow = [...draft.selectedByRow];
    selectedByRow[event.rowIndex] = event.value;
    return { selectedByRow };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    definition.pairs.every(
      (pair, index) =>
        (definition.dontKnow !== undefined &&
          draft.selectedByRow[index] === definition.dontKnow.value) ||
        pair.some(
          (option) =>
            option.value === draft.selectedByRow[index] && !option.disabled,
        ),
    )
      ? { valid: true }
      : { valid: false, error: copy.validation.chooseEachRow },
  encode: (definition, draft) => {
    const validation = gridThisOrThatCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const values = definition.pairs.map(
      (_, index) => draft.selectedByRow[index]!,
    );
    return {
      type: "grid_this_or_that",
      value: values.join(","),
      responses: encodeResponses(values),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = gridThisOrThatCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "grid_this_or_that",
      question: definition.prompt,
      answer: definition.pairs
        .map((pair, index) => {
          if (
            definition.dontKnow &&
            draft.selectedByRow[index] === definition.dontKnow.value
          ) {
            return `${index + 1}. ${definition.dontKnow.label}`;
          }
          const option = pair.find(
            (item) => item.value === draft.selectedByRow[index],
          )!;
          return `${index + 1}. ${option.label}`;
        })
        .join("\n"),
    };
  },
};
